import React, { useState, useRef, useEffect } from 'react';
import { Testimonial } from '../types';
import TestimonialCard from './TestimonialCard';

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ testimonials }) => {
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef<number | null>(null);
  const dragEndX = useRef<number | null>(null);

  const totalSlides = testimonials.length;

  // Start at the first "real" slide (after the cloned set)
  const [currentIndex, setCurrentIndex] = useState(totalSlides);

  // Create extended array: [clones of end] + [original] + [clones of start]
  const extendedTestimonials = [
    ...testimonials.slice(-totalSlides), // Clone all at start
    ...testimonials,                      // Original
    ...testimonials.slice(0, totalSlides), // Clone all at end
  ];

  // Update items per view based on screen size
  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth <= 768) {
        setItemsPerView(1);
      } else if (window.innerWidth <= 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  // Handle infinite loop reset
  useEffect(() => {
    if (!isTransitioning) return;

    const handleTransitionEnd = () => {
      setIsTransitioning(false);

      // If we've scrolled into the cloned area, jump to the real slides
      if (currentIndex >= totalSlides * 2) {
        // Jumped past the end clones, reset to beginning of real slides
        setCurrentIndex(currentIndex - totalSlides);
      } else if (currentIndex < totalSlides) {
        // Jumped before the start clones, reset to end of real slides
        setCurrentIndex(currentIndex + totalSlides);
      }
    };

    const track = trackRef.current;
    if (track) {
      track.addEventListener('transitionend', handleTransitionEnd);
      return () => track.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [isTransitioning, currentIndex, totalSlides]);

  const showNavigation = totalSlides > itemsPerView;

  const handlePrev = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDragEnd = () => {
    if (dragStartX.current === null || dragEndX.current === null) return;

    const diff = dragStartX.current - dragEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    dragStartX.current = null;
    dragEndX.current = null;
    setIsDragging(false);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartX.current = e.touches[0].clientX;
    dragEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    dragEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse events for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragEndX.current = null;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    dragEndX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    handleDragEnd();
  };

  const handleMouseLeave = () => {
    if (!isDragging) return;
    handleDragEnd();
  };

  // If 3 or fewer testimonials, just show them in a grid
  if (testimonials.length <= 3 && itemsPerView === 3) {
    return (
      <div className="grid grid-3" data-cms-list="testimonials">
        {testimonials.map((testimonial, index) => (
          <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="testimonial-carousel" data-cms-list="testimonials">
      <div
        className={`carousel-viewport${isDragging ? ' dragging' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={trackRef}
          className="carousel-track"
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
            transition: isTransitioning ? 'transform 0.4s ease-out' : 'none',
          }}
        >
          {extendedTestimonials.map((testimonial, index) => (
            <div
              key={`${testimonial.id}-${index}`}
              className="carousel-slide"
              style={{ flex: `0 0 ${100 / itemsPerView}%` }}
            >
              <TestimonialCard testimonial={testimonial} index={index % totalSlides} />
            </div>
          ))}
        </div>
      </div>

      {showNavigation && (
        <div className="carousel-dots">
          {testimonials.map((_, i) => {
            // Map currentIndex back to real slide index (0 to totalSlides-1)
            const realIndex = ((currentIndex - totalSlides) % totalSlides + totalSlides) % totalSlides;
            return (
              <button
                key={i}
                className={`carousel-dot ${i === realIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsTransitioning(true);
                  setCurrentIndex(totalSlides + i);
                }}
                aria-label={`Go to slide ${i + 1}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestimonialCarousel;
